/**
 * Shared contract for the Artifax status page.
 *
 * This module is the single source of truth for the `/api/status` payload
 * shape (the README calls it `status.json`). Both the Fastify backend and the
 * React frontend import these types so the wire format can never drift.
 */

/**
 * Service health states understood by the UI.
 *
 * StatusCake itself only reports binary up/down, so in the current build the
 * poller only ever emits `operational`, `major`, or `maintenance` (see the
 * "binary + maintenance" decision in the build plan). The remaining values are
 * kept in the union so the UI and contract are forward-compatible if we later
 * derive `degraded`/`partial` from latency or downtime thresholds.
 */
export type Status =
  | 'operational'
  | 'degraded'
  | 'partial'
  | 'major'
  | 'maintenance';

export const STATUS_VALUES: readonly Status[] = [
  'operational',
  'degraded',
  'partial',
  'major',
  'maintenance',
] as const;

/** UI region keys. Note these differ from the StatusCake tag names. */
export type RegionKey = 'EU' | 'US' | 'CA';

export const REGION_KEYS: readonly RegionKey[] = ['EU', 'US', 'CA'] as const;

/** One day in a 90-day uptime bar. */
export interface HistoryDay {
  /** ISO date (YYYY-MM-DD), UTC. */
  date: string;
  status: Status;
}

/** A single monitored service within a region (or globally). */
export interface ServiceStatus {
  status: Status;
  /** Rolling 90-day uptime percentage, e.g. 99.86. */
  uptime90: number;
  /** Oldest-to-newest, exactly 90 entries. */
  history: HistoryDay[];
  /** Daily average response time in ms, oldest-to-newest, up to 30 entries. */
  latencyMs: number[];
}

export interface RegionStatus {
  /** Human label, e.g. "UK/EU". */
  label: string;
  event: ServiceStatus;
}

export interface StatusPayload {
  /** ISO 8601 timestamp of when this snapshot's data was last refreshed. */
  updatedAt: string;
  /** Worst-of across all services, or `maintenance` during an active window. */
  overall: Status;
  /**
   * True when the backend is serving a last-known-good snapshot because the
   * StatusCake API was unreachable or rate-limited. `updatedAt` then reflects
   * the age of that snapshot, not "now".
   */
  stale: boolean;
  regions: Record<RegionKey, RegionStatus>;
  global: {
    agora: ServiceStatus;
  };
}

/** Fixed UI labels per region (left column / auto-detect copy). */
export const REGION_LABELS: Record<RegionKey, string> = {
  EU: 'UK/EU',
  US: 'US',
  CA: 'CA',
};

/** Full region names used in the "Auto-detected: …" and header copy. */
export const REGION_FULL_NAMES: Record<RegionKey, string> = {
  EU: 'UK/EU',
  US: 'United States',
  CA: 'Canada',
};
